import type { Lang, LorsCopy } from "./translations";

export type Oct22Headline = { headline: string; subhead: string };

export const oct22Headlines: Oct22Headline[] = [
  {
    headline: "📌 You thought the will handled it. In California, it barely starts the process.",
    subhead:
      "Find out what actually keeps your home out of probate — and why a will alone doesn't.",
  },
  {
    headline: "📌 The hardest part isn't the paperwork. It's what happens to your family without it.",
    subhead:
      "Join Ruby Steinbrecher for one hour that could save your family a year of stress.",
  },
  {
    headline: "📌 Owning your home and protecting it for your family are two completely different things",
    subhead:
      "A free virtual workshop on the one document that bridges the gap.",
  },
  {
    headline: "📌 A will tells a judge what you wanted. A trust means there is no judge.",
    subhead:
      "Most California homeowners don't know the difference. This free webinar makes it clear.",
  },
  {
    headline: "📌 Your family will handle this one day, with or without your help",
    subhead:
      "A free virtual workshop on the documents that keep your loved ones out of court and in control.",
  },
  {
    headline: "📌 The document most families rely on is the one that sends them to court",
    subhead:
      "Learn which documents California homeowners actually need — in one free hour.",
  },
  {
    headline: "📌 Probate doesn't take your house. It takes a year of your family's life and a percentage of everything you left them.",
    subhead:
      "One lunch hour to learn how to avoid all of it.",
  },
  {
    headline: "📌 This is the hour your family will be glad you spent",
    subhead:
      "A free online workshop that turns confusion into clarity — no jargon, no pressure, no courthouse.",
  },
  {
    headline: "📌 Love isn't a plan. But a plan is how you prove it.",
    subhead:
      "Learn how to protect your home and your family — in one lunch hour, from anywhere.",
  },
  {
    headline: "📌 Sixty minutes between \"I should probably do something\" and actually doing it",
    subhead:
      "A free virtual workshop on the basics every California homeowner needs to understand.",
  },
];

const baseEn = {
  eyebrow: "Free Estate Planning Webinar",
  headline: oct22Headlines[0].headline,
  headlineBreakBefore: "",
  subhead: oct22Headlines[0].subhead,
  subheadLabel: "A note from Ruby:",
  learnTitle: "What Most Families Don't Realize Until It's Too Late",
  learnItems: [
    "How wills, living trusts, and powers of attorney each work in California",
    "How a properly funded living trust can help a family pass assets without probate court",
    "What California's probate process generally looks like — timelines, court involvement, and typical costs",
    "Practical first steps for putting an estate plan in place, and the questions to ask before you do",
  ],
  quote:
    "Ruby Steinbrecher was fantastic. The family trust workshop was clear, informative, and extremely helpful for getting started with estate planning.",
  quoteAttribution: "Shawn H., Workshop Attendee",
  formDate: { short: "Thu, October 22nd", long: "Thursday, October 22nd" },
  formTime: "12:00 PM PT",
  formVenueLine1: "Live Online Webinar",
  formVenueLine2: "Join from anywhere — link sent after registration",
  formTitle: "Reserve Your Seat",
  formSubtitle: "Free to attend. Limited seating available.",
  formFirstName: "First name",
  formLastName: "Last name",
  formEmail: "Email",
  formPhone: "Phone",
  formSmsConsent:
    "I consent to receive text messages from the Law Office of Ruby Steinbrecher about this event, and occasional marketing messages. Message & data rates may apply. Text HELP for assistance, reply STOP to opt out.",
  formEnewsConsent:
    "I'd like to receive occasional email updates from the Law Office of Ruby Steinbrecher.",
  formPrivacy: "Privacy Policy",
  formSubmit: "Reserve My Seat",
  formSubmitting: "Reserving your seat…",
  formTrust: "⭐ Trusted by local families | 81 Five-Star Reviews",
  getDirections: "",
  footerCopyright: "© 2026 Ruby Steinbrecher",
  footerPrivacy: "Privacy Policy",
  testimonialsTitle: "What Families Are Saying",
  aboutTitle: "Meet Ruby Steinbrecher",
  aboutRole: "Attorney",
  aboutBody: [
    "I have practiced law since 2007, first working at a medium-sized firm in San Francisco handling civil litigation, and then moving into estate planning, small business and contract law since 2010. I value people, the planet, and prosperity that does not adversely affect the former.",
    "Prior to entering the legal field, I had a variety of business and life experience that has only enhanced my ability to serve clients: Road Manager for Ken Kesey\'s bus trip to Great Britain in 1999, Human Resources at a tech startup in 2001, recognized for my work as a Case Manager at Casey Family Programs with disadvantaged youth in the foster care system in Oakland and the wider San Francisco-Bay Area in the early 2000\'s.",
    "Currently, I live in Sebastopol with my daughter, husband, and a menagerie of pets, where I enjoy gardening as it keeps me grounded and enjoying the beautiful weather.",
  ],
  registrationTitle: "Ready to Secure Your Family's Future?",
  registrationSub: "Free to attend. Limited seating available.",
  registrationCta: "Save My Seat",
  registrationFoot:
    "No pressure. Just clear information so you can make the right decision for your family.",
  errorTitle: "Something went wrong",
  errorBody:
    "We couldn't reserve your seat. Please try again or call our office.",
  validation: {
    firstName: "First name is required",
    lastName: "Last name is required",
    email: "Enter a valid email",
    phone: "Enter a valid phone number",
  },
  timeAgo: {
    monthAgo: "a month ago",
    monthsAgo: (n: number) => `${n} months ago`,
    yearAgo: "a year ago",
  },
};

export const lorsContentOct22 = { en: baseEn, es: baseEn } as unknown as Record<Lang, LorsCopy>;

export const getOct22Copy = (hParam: string | null): LorsCopy => {
  const n = Number(hParam);
  const idx = Number.isInteger(n) && n >= 1 && n <= oct22Headlines.length ? n - 1 : 0;
  const pick = oct22Headlines[idx];
  return { ...baseEn, headline: pick.headline, subhead: pick.subhead } as unknown as LorsCopy;
};
